import { useQuery } from "@tanstack/react-query";
import {
  fetchBespokeTenorAPI,
  fetchCommoditiesAPI,
  fetchConstraintsAPI,
  fetchContractStylesAPI,
  fetchTenorAPI,
  fetchTypes,
} from "api/pricerApi";
import { FIELDS_LABEL } from "constants/addOption";
import { parseShortcutsPayload } from "helpers/Pricer/addOptionShortcuts";

const usePricerFetchFunctions = (addOptionData, bespokePeriodId = 1) => {
  const { data: commodityData, isLoading: commodityLoading } = useQuery(
    [FIELDS_LABEL.COMMODITY.key],
    async () => {
      const res = await fetchCommoditiesAPI();
      return parseShortcutsPayload(res?.data?.data);
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      cacheTime: 0,
    }
  );

  const { data: contractStyleData, isLoading: contractStyleLoading } = useQuery(
    [
      FIELDS_LABEL.CONTRACT_STYLE.key,
      {
        variable: addOptionData[FIELDS_LABEL.COMMODITY.key],
        skip: !addOptionData[FIELDS_LABEL.COMMODITY.key],
      },
    ],
    async () => {
      const res = await fetchContractStylesAPI(
        addOptionData[FIELDS_LABEL.COMMODITY.key]
      );
      return res.data.data.map((item) => ({
        value: item.title,
        label: item.title,
      }));
    },
    {
      enabled: false, // !!addOptionData[FIELDS_LABEL.COMMODITY.key] === true,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const { data: tenorResponse } = useQuery(
    [
      FIELDS_LABEL.TENOR.key,
      { variable: addOptionData[FIELDS_LABEL.COMMODITY.key]?.value },
    ],
    async () => {
      const tenorData = await fetchTenorAPI(
        addOptionData[FIELDS_LABEL.COMMODITY.key]?.value
      );
      const tenors = {};
      const periods = [];
      tenorData.data.data.forEach((item) => {
        tenors[item["period_title"]] = item.tenors;
        if (item.period_id != null) {
          periods.push({ id: item.period_id, title: item.period_title });
        }
      });
      return { tenors, periods };
    },
    {
      enabled:
        !!addOptionData[FIELDS_LABEL.TYPE.key] === true &&
        !!addOptionData[FIELDS_LABEL.COMMODITY.key]?.value === true,
      refetchOnWindowFocus: false,
    }
  );
  const { data: bespokeTenors, isFetching: bespokeTenorsLoading } = useQuery(
    [
      FIELDS_LABEL.BESPOKE_TENOR.key,
      { variable: addOptionData[FIELDS_LABEL.COMMODITY.key]?.value, periodId: bespokePeriodId },
    ],
    async () => {
      const res = await fetchBespokeTenorAPI(
        addOptionData[FIELDS_LABEL.COMMODITY.key]?.value,
        bespokePeriodId
      );
      return res.data.data;
    },
    {
      enabled:
        !!addOptionData[FIELDS_LABEL.TYPE.key] === true &&
        !!addOptionData[FIELDS_LABEL.COMMODITY.key]?.value === true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: typesData, isLoading: typesLoading } = useQuery(
    [FIELDS_LABEL.TYPE.key, { variable: addOptionData[FIELDS_LABEL.LEGS.key] }],
    async () => {
      const result = await fetchTypes(addOptionData[FIELDS_LABEL.LEGS.key]);
      return result?.data.data.map((item) => ({
        label: item.title,
        value: item.id,
      }));
    },
    {
      enabled: !!addOptionData[FIELDS_LABEL.LEGS.key] === true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: constraintsData } = useQuery(
    [
      FIELDS_LABEL.CONSTRAINTS.key,
      { variable: addOptionData[FIELDS_LABEL.TYPE.key] },
      { variable: addOptionData[FIELDS_LABEL.COMMODITY.key]?.value },
    ],
    async () => {
      const constraintsData = await fetchConstraintsAPI(
        addOptionData[FIELDS_LABEL.TYPE.key],
        addOptionData[FIELDS_LABEL.LEGS.key],
        addOptionData[FIELDS_LABEL.COMMODITY.key]?.value
      );
      return constraintsData.data.data;
    },
    {
      enabled:
        !!addOptionData[FIELDS_LABEL.TYPE.key] === true &&
        !!addOptionData[FIELDS_LABEL.LEGS.key] === true &&
        !!addOptionData[FIELDS_LABEL.COMMODITY.key]?.value === true,
      refetchOnWindowFocus: false,
    }
  );

  return {
    [FIELDS_LABEL.COMMODITY.key]: commodityData,
    [FIELDS_LABEL.CONTRACT_STYLE.key]: contractStyleData,
    [FIELDS_LABEL.TENOR.key]: tenorResponse?.tenors,
    [FIELDS_LABEL.TYPE.key]: typesData,
    [FIELDS_LABEL.CONSTRAINTS.key]: constraintsData,
    [FIELDS_LABEL.BESPOKE_TENOR.key]: bespokeTenors,
    bespokeTenorsLoading,
    bespokePeriods: tenorResponse?.periods ?? [],
    commodityLoading,
    contractStyleLoading,
    typesLoading,
  };
};

export default usePricerFetchFunctions;
